import React from "react";
import { useTranslation } from "react-i18next";
import { BADGE_TEXT_CLASS, BadgePill } from "./BadgePill";
import { formatLastUpdatedHKT } from "./handler/badge.helper";
import { formatMinutes } from "./handler/time.helper";
import { FlightBadgeState, FlightFixture } from "./types";

export interface ArrivalResultCardProps {
  fixture: FlightFixture;
  badge: FlightBadgeState;
}

export const ArrivalResultCard: React.FC<ArrivalResultCardProps> = ({
  fixture,
  badge,
}) => {
  const { t } = useTranslation("flightAlerts");
  // The backend's own badgeDelayMinutes, passed straight through — never a local
  // `estimatedMinutes - scheduledMinutes` recompute (that's where the midnight-crossing bug
  // lived; see badge.helper.ts).
  const diffMinutes = fixture.badgeDelayMinutes ?? undefined;
  const lastUpdatedTime = formatLastUpdatedHKT(fixture.lastUpdated);

  const showLanded = badge === "landed";
  const showCancelled = badge === "cancelled";
  const showStatusUnavailable = badge === "statusUnavailable";
  const hasEstimate = fixture.estimatedMinutes !== undefined;
  const showScheduleRow =
    showLanded || showCancelled || showStatusUnavailable || hasEstimate;

  return (
    <div className="flex w-full flex-col gap-[24px] rounded-[16px] border-2 border-ink-12 bg-card p-[32px]">
      <div className="flex max-[500px]:flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <span className="inline-flex w-fit shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-ink-100 py-[5px] pl-[10px] pr-[10px]">
            <span className="font-material-symbols text-[14px] leading-none text-white">
              flight_land
            </span>
            <span className="font-lexend text-[10px]/[12px] font-bold tracking-[0.6px] uppercase text-white">
              {t("flight.badgeArriving")}
            </span>
          </span>

          <h2 className="font-lexend text-[28px]/[32px] font-bold tracking-[-1px] text-ink-100">
            {fixture.flightNumber}
          </h2>

          <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-ink-60">
            {t("results.arrivalSubtitle", {
              airport: fixture.otherAirport,
              hall: fixture.hall ?? "—",
              date: fixture.dateDisplay,
            })}
          </p>

          {fixture.arrivalFieldsUnavailableNote && (
            <p className="font-lexend text-[12px]/[16px] font-normal tracking-normal text-ink-55">
              {t("results.arrivalFieldsUnavailableNote")}
            </p>
          )}
        </div>

        <BadgePill
          badge={badge}
          {...(diffMinutes !== undefined ? { diffMinutes } : {})}
        />
      </div>

      <div className="h-px w-full bg-ink-10" />

      {showScheduleRow ? (
        <div className="flex items-end gap-x-6 gap-y-4 max-[355px]:flex-wrap">
          <div className="flex flex-col gap-1">
            <span className="font-lexend text-[12px]/[16px] font-normal tracking-normal text-ink-60">
              {t("results.scheduledLabelArrival")}
            </span>

            <span className="font-lexend text-[40px]/[40px] md:text-[56px]/[56px] font-bold tracking-[-1.2px] md:tracking-[-1.8px] text-ink-32 line-through">
              {formatMinutes(fixture.scheduledMinutes)}
            </span>
          </div>

          <span className="font-material-symbols shrink-0 pb-2 text-[32px] leading-none text-ink-40">
            arrow_forward
          </span>

          <div className="flex flex-col gap-1">
            <span
              className={`font-lexend text-[12px]/[16px] font-normal tracking-normal ${
                showLanded ? "text-flight-neutral" : BADGE_TEXT_CLASS[badge]
              }`}
            >
              {showLanded
                ? t("results.landedAtLabel")
                : t("results.estimatedLabelArrival")}
            </span>

            <span className="font-lexend text-[40px]/[40px] md:text-[56px]/[56px] font-bold tracking-[-1.2px] md:tracking-[-1.8px] text-ink-100">
              {showLanded
                ? formatMinutes(fixture.resolvedTimeMinutes!)
                : showCancelled || showStatusUnavailable
                  ? "—"
                  : formatMinutes(fixture.estimatedMinutes!)}
            </span>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          <span className="font-lexend text-[12px]/[16px] font-normal tracking-normal text-ink-60">
            {t("results.scheduledLabelArrival")}
          </span>
          <span className="font-lexend text-[40px]/[40px] md:text-[56px]/[56px] font-bold tracking-[-1.2px] md:tracking-[-1.8px] text-ink-100">
            {formatMinutes(fixture.scheduledMinutes)}
          </span>
        </div>
      )}

      {showStatusUnavailable && (
        <div className="rounded-lg bg-flight-neutral-bg px-4 py-3 text-sm text-mineshaft-700">
          {t("results.statusUnavailableBody")}
        </div>
      )}

      {lastUpdatedTime && (
        <p className="font-lexend text-[12px]/[16px] font-normal tracking-normal text-ink-60">
          {t("results.lastUpdated", { time: lastUpdatedTime })}
        </p>
      )}
    </div>
  );
};
